import { alpha, Box, Grid, Stack, Typography, useTheme } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useBatchUpdateItems } from 'api/aws/batch/useBatchUpdateItems'
import { useContactsItems } from 'api/aws/contacts/useContactItems'
import { useOrganizationView } from 'api/aws/organizations_table/useOrganizationView'
import { useUserItems } from 'api/aws/users/useUserItems'
import { useEntitiesFromSearch } from 'api/search'
import { toServiceKey } from 'components/DataGrid/operators/formatKeys'
import { formatCurrency } from 'components/DataGrid/operators/formatNumbers'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { GROBxLoader, ProfileCardAdapter, ProfileFooter, UniversalReadOnlyField } from 'theme/components'
import { CustomProductDraft, Organization, OrgTableUpdateItem, Product, UpdateField, User } from 'types'
import { isRunningToday } from 'utils/isRunningToday'
import { v4 as uuidv4 } from 'uuid'
import { useUniversalNotify } from '../../api/aws/notifications/useUniversalNotify'
import { useSyncQueryCache } from '../../api/aws/organizations_table/useSyncQueryCache'
import { useSubmitTask } from '../../api/aws/tasks/useSubmitTask'
import { useUpdateItems } from '../../api/aws/useUpdateItems'
import { useUpdateWebsite } from '../../api/aws/useUpdateWebsite'
import { useCreateGoogleFolders } from '../../api/google/useCreateGoogleFolders'
import { useLayoutContext } from '../../context/LayoutProvider/LayoutProvider'
import { useSnackbarContext } from '../../context/SnackbarProvider/SnackbarProvider'
import { useValidationContext } from '../../context/ValidationProvider/ValidationProvider'
import CancelButton from '../ui/cancel-button'
import EditIconButton from '../ui/edit-icon-buttons'
import { SaveChangesButton } from '../ui/save-changes-button'
import { fieldsConfigSections } from './fieldConfigSections'
import { parentConfigFromChildSections } from './parentFieldConfigSections'
import { gridStyles } from './style'
import { normalizeContractedBy } from './utils'

type ChangedFieldMap = Record<string, { newValue: any; isDirty: boolean }>
const prettifyKey = (key: string) => {
  const parts = key.match(/[A-Z]?[a-z]+|[A-Z]+(?![a-z])/g)
  return parts ? parts.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : key
}

const OrganizationProfile = () => {
  const { PK } = useParams()
  const { pinned } = useLayoutContext()
  const queryClient = useQueryClient()
  const { isValidWebsite } = useValidationContext()
  const { showSnackbar } = useSnackbarContext()
  const theme = useTheme()
  const { organizations, isItemsLoading, custom_products } = useEntitiesFromSearch()
  const { organization, isParent, existingCustomProducts, viewProducts } = useOrganizationView(PK)
  const { data: users, isLoading: isUsersLoading } = useUserItems()
  const { data: contacts, isLoading: isContactsLoading } = useContactsItems()
  const {
    syncOrganizationFields,
    patchOrganizationFieldsInCache,
    patchBootstrapCustomProductsCache,
  } = useSyncQueryCache()
  const { mutate: _updateItems } = useUpdateItems()
  const { mutate: batchUpdateItems } = useBatchUpdateItems(['organization', PK])
  const { mutate: updateWebsite } = useUpdateWebsite()
  const { mutate: _createGoogleFolders } = useCreateGoogleFolders()
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  const { notify } = useUniversalNotify()
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [_updateData, _setUpdateData] = useState<UpdateField[]>([])
  const [savingFields, setSavingFields] = useState<string[]>([])
  const [changedFields, setChangedFields] = useState<ChangedFieldMap>({})
  const initialTeamDetails = organization?.team_details ?? []
  const [teamDetailsValue, setTeamDetailsValue] = useState<User[]>(initialTeamDetails)
  const { submitNewTask } = useSubmitTask()
  const currentUser: User | null = JSON.parse(localStorage.getItem('currentUser') || 'null')
  const location = useLocation()
  ////////////////// CUSTOM_PRODUCTS //////////////////////
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [customProductDrafts, setCustomProductDrafts] = useState<CustomProductDraft[]>([])
  const hasDirtyOrgFields = Object.values(changedFields).some(v => v?.isDirty)
  const hasDirtyCustomDrafts = customProductDrafts.some(d => d?.isDirty)
  const serviceKey = toServiceKey(selectedProduct) // stable
  const _SK = `PRODUCT_CUSTOM_${serviceKey}`
  const { monthlyTotal, grossProfit, spend, implementationFee, total } = organization?.totals ?? {
    monthlyTotal: 0,
    grossProfit: 0,
    spend: 0,
    implementationFee: 0,
    total: 0,
  }
  const toDraftFromDb = (p: any): CustomProductDraft => ({
    id: p.SK, // keep stable
    PK: p.PK,
    SK: p.SK,
    productName: p.productName ?? '',
    description: p.description ?? '',
    category: p.category ?? '',
    subcategory: p.subcategory ?? '',
    owner: p.owner ?? null,
    price: Number(p.price ?? 0),
    margin: Number(p.margin ?? 0),
    isNew: false,
    isDirty: false,
  })

  const existingDrafts = useMemo(() => (existingCustomProducts ?? []).map(toDraftFromDb), [existingCustomProducts])
  // merge: prefer local draft if user already added/edited same SK
  const mergedCustomDrafts = useMemo(() => {
    const map = new Map<string, CustomProductDraft>()
    existingDrafts.forEach(d => map.set(d.id, d))
    customProductDrafts.forEach(d => map.set(d.id, d))
    return Array.from(map.values()).filter(d => !d.isDeleted)
  }, [existingDrafts, customProductDrafts])

  const selectCustomProduct = (newValue: Product) => {
    setSelectedProduct(newValue)
  }
  // const handleAddCustomProduct = (catalogProduct: any) => {
  //   if (!catalogProduct || !PK) return

  //   const draft = makeDraftFromCatalog(catalogProduct)

  //   setCustomProductDrafts(prev => {
  //     const already = prev.some(x => x.SK === draft.SK)
  //     if (already) return prev
  //     return [...prev, { ...draft, isNew: true, isDirty: true }]
  //   })
  // }
  const handleAddCustomProduct = (catalogProduct: any) => {
    if (!catalogProduct || !PK) return

    const serviceKey = toServiceKey(catalogProduct)
    if (!serviceKey) return

    const SK = `PRODUCT_CUSTOM_${serviceKey}`

    // find existing DB record (including archived)
    const existing = (custom_products ?? []).filter(p => p?.PK === PK).find(p => p?.SK === SK)

    // if exists and archived -> reuse by unarchiving
    if (existing?.isArchived) {
      const fromDb = toDraftFromDb({ ...existing, isArchived: false })
      setCustomProductDrafts(prev => {
        // seed draft if not already present
        const already = prev.some(x => x.SK === SK)
        if (already) return prev.map(d => (d.SK === SK ? { ...d, isDeleted: false, isDirty: true } : d))
        return [...prev, { ...fromDb, isDirty: true, isDeleted: false }]
      })
      return
    }

    // normal add
    const draft = makeDraftFromCatalog(catalogProduct)
    setCustomProductDrafts(prev => {
      const already = prev.some(x => x.SK === draft.SK)
      if (already) return prev
      return [...prev, { ...draft, isNew: !existing, isDirty: true }]
    })
  }

  const handleEditCustomDraft = (id: string, patch: Partial<CustomProductDraft>) => {
    setCustomProductDrafts(prev => {
      const existing = prev.find(d => d.id === id)
      if (existing) return prev.map(d => (d.id === id ? { ...d, ...patch, isDirty: true } : d))

      // If it wasn't already in local state, seed it from DB first:
      const fromDb = existingDrafts.find(d => d.id === id)
      if (!fromDb) return prev

      return [...prev, { ...fromDb, ...patch, isDirty: true }]
    })
  }

  const handleRemoveCustomDraft = (id: string) => {
    setCustomProductDrafts(prev => {
      const existing = prev.find(d => d.id === id)
      if (existing) return prev.map(d => (d.id === id ? { ...d, isDeleted: true, isDirty: true } : d))

      // deleting a DB-only row: seed it as deleted so save will delete it
      const fromDb = existingDrafts.find(d => d.id === id)
      return fromDb ? [...prev, { ...fromDb, isDeleted: true, isDirty: true }] : prev
    })
  }

  const makeDraftFromCatalog = (p: any): CustomProductDraft => {
    const SK = `PRODUCT_CUSTOM_${toServiceKey(p?.productName || p?.title)}`

    return {
      id: SK,
      PK: PK,
      SK: SK,
      productName: p?.productName ?? p?.title ?? '',
      description: p?.description ?? '',
      category: p?.category_name ?? '',
      subcategory: p?.subcategory ?? '',
      owner: p?.owner ?? null,
      price: Number(p?.price ?? 0),
      margin: Number(p?.margin ?? 0),
    }
  }
  const draftToBootstrapProduct = (d: CustomProductDraft): any => ({
    EntityType: 'product',
    PK: d.PK,
    SK: d.SK,
    serviceKey: d.SK.replace('PRODUCT_CUSTOM_', ''),
    productName: d.productName,
    description: d.description,
    category: d.category,
    subcategory: d.subcategory,
    owner: d.owner ?? null,
    price: Number(d.price ?? 0),
    margin: Number(d.margin ?? 0),
    isArchived: false,
    updatedAt: Date.now(),
    updatedBy: currentUser?.user_uuid ?? '',
  })
  const buildCustomProductUpdates = (drafts: CustomProductDraft[]) => {
    if (!organization?.PK) return []

    const now = Date.now()
    const dirtyDrafts = drafts.filter(d => d?.isDirty)

    return dirtyDrafts.flatMap(d => [
      { tableName: 'ORGANIZATIONS_TABLE', PK: d.PK, SK: d.SK, fieldName: 'EntityType', newValue: 'product' },
      {
        tableName: 'ORGANIZATIONS_TABLE',
        PK: d.PK,
        SK: d.SK,
        fieldName: 'serviceKey',
        newValue: d.SK.replace('PRODUCT_CUSTOM_', ''),
      },

      { tableName: 'ORGANIZATIONS_TABLE', PK: d.PK, SK: d.SK, fieldName: 'productName', newValue: d.productName },
      { tableName: 'ORGANIZATIONS_TABLE', PK: d.PK, SK: d.SK, fieldName: 'category', newValue: d.category },
      { tableName: 'ORGANIZATIONS_TABLE', PK: d.PK, SK: d.SK, fieldName: 'subcategory', newValue: d.subcategory },
      { tableName: 'ORGANIZATIONS_TABLE', PK: d.PK, SK: d.SK, fieldName: 'description', newValue: d.description },

      { tableName: 'ORGANIZATIONS_TABLE', PK: d.PK, SK: d.SK, fieldName: 'price', newValue: Number(d.price) || 0 },
      { tableName: 'ORGANIZATIONS_TABLE', PK: d.PK, SK: d.SK, fieldName: 'margin', newValue: Number(d.margin) || 0 },

      { tableName: 'ORGANIZATIONS_TABLE', PK: d.PK, SK: d.SK, fieldName: 'updatedAt', newValue: now },
      {
        tableName: 'ORGANIZATIONS_TABLE',
        PK: d.PK,
        SK: d.SK,
        fieldName: 'updatedBy',
        newValue: currentUser?.user_uuid ?? '',
      },
      { tableName: 'ORGANIZATIONS_TABLE', PK: d.PK, SK: d.SK, fieldName: 'isArchived', newValue: false },
    ])
  }
  const buildCustomWrites = (drafts: CustomProductDraft[]) => {
    const dirty = drafts.filter(d => d.isDirty)

    const deletes = dirty.filter(d => d.isDeleted)
    const upserts = dirty.filter(d => !d.isDeleted)

    const updates = buildCustomProductUpdates(upserts) // your current flatMap

    return { updates, deletes }
  }

  ////////////////////////////////////////////////////////

  useEffect(() => {
    setTeamDetailsValue(organization?.team_details ?? [])
  }, [organization?.team_details])
  useEffect(() => {
    if (organization) {
      console.log('organization', organization)
    }
  }, [])
  const getFieldValue = (fieldName: string) => {
    // team_details is managed separately
    if (fieldName === 'team_details') return teamDetailsValue

    // Handle nested field paths like 'team.AM'
    if (fieldName.startsWith('team.')) {
      const roleKey = fieldName.split('.')[1]
      const changed = changedFields[fieldName]?.newValue
      const original = safeOrg?.team?.[roleKey as keyof typeof safeOrg.team]
      const val = changed ?? original
      return Array.isArray(val) ? val : []
    }

    const changed = changedFields[fieldName]?.newValue
    const original = safeOrg?.[fieldName as keyof Organization]
    const arrayIntent = ARRAY_FIELDS.has(fieldName)

    const val = changed ?? original

    if (arrayIntent) return Array.isArray(val) ? val.filter(v => v != null && v !== '') : []
    if (Array.isArray(val)) return val.filter(v => v != null && v !== '')
    return val ?? ''
  }
  useEffect(() => {
    if (organization && !organization.team_details) {
      setChangedFields(prev => ({
        ...prev,
        team_details: { newValue: [], isDirty: false },
      }))
    }
  }, [organization])
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  const ARRAY_FIELDS = new Set([
    'contractedBy',
    'affiliated',
    'owner',
    'parentClient',
    'contacts',
    'teamMemberIds',
    'team_details',
  ])

  const safeOrg = useMemo(() => {
    if (!organization) return organization
    const withArrays: any = { ...organization }
    for (const key of ARRAY_FIELDS) {
      if (!Array.isArray(withArrays[key])) withArrays[key] = []
    }
    // Ensure team object exists with all role arrays initialized
    const teamRoles = ['AM', 'PM', 'DMS', 'SEO', 'MIA', 'BDE', 'QA', 'other']
    if (!withArrays.team || typeof withArrays.team !== 'object') {
      withArrays.team = {}
    }
    for (const role of teamRoles) {
      if (!Array.isArray(withArrays.team[role])) {
        withArrays.team[role] = []
      }
    }
    return withArrays
  }, [organization])
  const toId = (item: any) => {
    if (item == null) return null
    if (typeof item === 'string') return item.trim() || null
    if (typeof item === 'object')
      return item.id ?? item.contacts_uuid ?? item.org_uuid ?? item.orgId ?? item.value ?? null
    return null
  }
  const normalizeIdArray = (arr: any): string[] => {
    if (!Array.isArray(arr)) return []
    const ids = arr.map(toId).filter((v): v is string => !!v) // drop null/undefined/empty
    return Array.from(new Set(ids)) // dedupe
  }
  const handleChange = (fieldName: string, newValue: any) => {
    if (typeof fieldName !== 'string') return

    const isTeamDetailsField = fieldName === 'team_details'
    if (isTeamDetailsField) setTeamDetailsValue(newValue)

    // Handle nested team fields like 'team.AM'
    const isTeamRoleField = fieldName.startsWith('team.')
    let originalValue: any
    if (isTeamRoleField) {
      const roleKey = fieldName.split('.')[1]
      originalValue = safeOrg?.team?.[roleKey as keyof typeof safeOrg.team] ?? []
    } else {
      originalValue = safeOrg?.[fieldName as keyof Organization]
    }

    console.log('originalValue', originalValue)
    const normalizedNewValue = isTeamDetailsField
      ? newValue
      : isTeamRoleField
      ? Array.isArray(newValue)
        ? newValue
        : []
      : fieldName === 'contractedBy'
      ? normalizeContractedBy(newValue, organizations ?? [])
      : Array.isArray(newValue)
      ? normalizeIdArray(newValue)
      : newValue ?? ''
    console.log('normalizedNewValue', normalizedNewValue)

    const sameArray = (a: any[], b: any[]) =>
      Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every(v => b.includes(v))

    const isDirty =
      Array.isArray(normalizedNewValue) && Array.isArray(originalValue)
        ? !sameArray(normalizedNewValue, originalValue as any[])
        : normalizedNewValue !== originalValue

    setChangedFields(prev => ({
      ...prev,
      [fieldName]: { newValue: normalizedNewValue, isDirty },
    }))
  }
  useEffect(() => {
    if (changedFields) console.log('changedFields', changedFields)
  }, [changedFields])
  const handleUpdate = async () => {
    if (!organization?.PK || !organization?.SK) return

    // 1) org + custom detection
    const orgDirtyEntries = Object.entries(changedFields).filter(([_, meta]) => meta?.isDirty)

    const { updates: customUpserts, deletes: customDeletes } = buildCustomWrites(customProductDrafts)
    const hasOrgUpdates = orgDirtyEntries.length > 0
    const hasCustomUpdates = customUpserts.length > 0 || customDeletes.length > 0

    if (!hasOrgUpdates && !hasCustomUpdates) {
      console.warn('No updates found')
      return
    }

    // Optional: only show field spinners for org field updates
    if (hasOrgUpdates) setSavingFields(orgDirtyEntries.map(([fieldName]) => fieldName))

    // 2) split org updates like you do today
    const websiteEntry = orgDirtyEntries.find(([fieldName]) => fieldName === 'website')
    const teamEntry = orgDirtyEntries.find(([fieldName]) => fieldName === 'team_details')
    const contractedByEntry = orgDirtyEntries.find(([fieldName]) => fieldName === 'contractedBy')

    // Separate team role entries (team.AM, team.PM, etc.) from other entries
    const teamRoleEntries = orgDirtyEntries.filter(([fieldName]) => fieldName.startsWith('team.'))
    const orgNonWebsiteEntries = orgDirtyEntries.filter(
      ([fieldName]) => fieldName !== 'website' && !fieldName.startsWith('team.')
    )

    // handle website separately (same as you already do)
    if (websiteEntry) {
      const [fieldName, meta] = websiteEntry
      handleUpdateWebsite(fieldName, meta.newValue)
    }

    // 3) team notify side-effect (same logic, but do it even if ONLY custom updates exist)
    if (teamEntry) {
      const [_, meta] = teamEntry
      const originalTeam = organization?.team_details || []
      const newTeam = meta?.newValue || []

      const originalIds = originalTeam.map(m => m.user_uuid)
      const newIds = newTeam.map(m => m.user_uuid)

      const newlyAddedIds = newIds.filter(id => !originalIds.includes(id))
      const selectedUsers = users?.filter(u => newlyAddedIds.includes(u.user_uuid)) || []

      if (newlyAddedIds.length > 0) {
        await notify({
          data: {
            users: selectedUsers ?? [],
            type: 'team',
            severity: 'success',
            title: 'Added Notification',
            message: `Added to team | ${organization?.name}`,
            board: 'organizations',
            boardItemName: organization?.name,
            boardId: organization?.org_uuid,
            slackHeaderMessage: 'Added to team',
            slackMessageHtml: `Added to team | ${organization?.name}`,
            slackDirectLink: `${location.pathname}`,
            sendSlack: true,
            sendApp: true,
          },
        })
      }
    }

    // 4) contractedBy task side-effect (same logic)
    if (contractedByEntry && organization?.isOnboardingComplete) {
      const [_, meta] = contractedByEntry
      const original = organization?.contractedBy ?? []
      const newValue = meta?.newValue ?? []
      const newContractedByIds = newValue.filter(id => !original.includes(id))

      if (newContractedByIds.length > 0) {
        const taskSK = `TASK_${uuidv4()}`
        const task = {
          PK: `ORG_${organization?.org_uuid}`,
          SK: taskSK,
          type: 'Review / Audit',
          taskType: 'Review / Audit',
          name: 'Review / Audit',
          channel: 'Digital',
          isMarketing: true,
          isDigital: true,
          masterStatus: 'Not Started',
          orgId: organization?.org_uuid,
          assigner: {
            firstName: currentUser?.firstName,
            id: currentUser?.id,
            image_512: currentUser?.image_512,
            lastName: currentUser?.lastName,
            name: currentUser?.name,
            role: currentUser?.role,
            title: currentUser?.title,
            user_uuid: currentUser?.user_uuid,
          },
          assignees: currentUser ? [currentUser] : [],
          addtlNotes: `<p>You updated the Contracted By field for ${organization?.name}, please notify the production team<p>`,
        }

        await submitNewTask({ formData: task, setIsCreating: setIsEditing, onClose: () => {} })

        await notify({
          data: {
            users: currentUser ? [currentUser] : [],
            type: 'task',
            severity: 'success',
            title: 'Task',
            message: `New task assigned`,
            board: 'organizations',
            boardItemName: organization?.name,
            boardId: organization?.org_uuid,
            slackHeaderMessage: 'New task assigned',
            slackMessageHtml: `You changed the Property Management Company for ${organization?.name}, please notify the production team.`,
            slackDirectLink: `${location.pathname}`,
            sendSlack: true,
            sendApp: true,
            includeTaskLink: true,
            task,
          },
        })
      }
    }

    // 5) Build the org updates for Dynamo (excluding website)
    const formattedOrgUpdates: OrgTableUpdateItem[] = orgNonWebsiteEntries.map(([fieldName, meta]) => ({
      tableName: 'ORGANIZATIONS_TABLE',
      PK: organization.PK,
      SK: organization.SK,
      fieldName,
      newValue: Array.isArray(meta.newValue) ? meta.newValue.filter((v: any) => v != null && v !== '') : meta.newValue,
    }))

    // 5b) Build team object update from team role entries (team.AM, team.PM, etc.)
    if (teamRoleEntries.length > 0) {
      // Start with existing team object or empty
      const existingTeam = organization?.team || {}
      const updatedTeam = { ...existingTeam }

      // Merge in all changed team role values
      for (const [fieldName, meta] of teamRoleEntries) {
        const roleKey = fieldName.split('.')[1] // 'AM', 'PM', etc.
        updatedTeam[roleKey] = Array.isArray(meta.newValue) ? meta.newValue : []
      }

      formattedOrgUpdates.push({
        tableName: 'ORGANIZATIONS_TABLE',
        PK: organization.PK,
        SK: organization.SK,
        fieldName: 'team',
        newValue: updatedTeam,
      })
    }
    const customDeleteUpdates: OrgTableUpdateItem[] = customDeletes.flatMap(d => [
      { tableName: 'ORGANIZATIONS_TABLE', PK: d.PK, SK: d.SK, fieldName: 'isArchived', newValue: true },
      { tableName: 'ORGANIZATIONS_TABLE', PK: d.PK, SK: d.SK, fieldName: 'updatedAt', newValue: Date.now() },
      {
        tableName: 'ORGANIZATIONS_TABLE',
        PK: d.PK,
        SK: d.SK,
        fieldName: 'updatedBy',
        newValue: currentUser?.user_uuid ?? '',
      },
    ])

    // patch list for cache (only org item updates)
    const patches = formattedOrgUpdates.map(u => ({ fieldName: u.fieldName, newValue: u.newValue }))

    // 6) THIS is the key: merge org + custom
    const allUpdates: OrgTableUpdateItem[] = [...formattedOrgUpdates, ...customUpserts, ...customDeleteUpdates]

    // If the only updates were "website" (handled separately), we might have nothing left to batch write:
    if (allUpdates.length === 0) {
      setSavingFields([])
      setIsEditing(false)
      return
    }

    batchUpdateItems(
      { updates: allUpdates },
      {
        onSuccess: () => {
          showSnackbar('Update successful', 'success')

          // clear org edits
          setChangedFields({})
          setSavingFields([])
          setIsEditing(false)

          // mark drafts clean (or clear them)
          setCustomProductDrafts(prev => prev.map(d => ({ ...d, isDirty: false, isNew: false })))
          // or: setCustomProductDrafts([])

          // patch org cache only (custom products are different SKs)
          if (patches.length) {
            patchOrganizationFieldsInCache(queryClient, organization.PK, organization.SK, patches)
          }
          const upsertDrafts = customProductDrafts.filter(d => d.isDirty && !d.isDeleted)
          const deleteDrafts = customProductDrafts.filter(d => d.isDirty && d.isDeleted)
          patchBootstrapCustomProductsCache(queryClient, {
            upserts: upsertDrafts.map(draftToBootstrapProduct),
            archives: deleteDrafts.map(d => ({
              PK: d.PK,
              SK: d.SK,
              isArchived: true,
              updatedAt: Date.now(),
              updatedBy: currentUser?.user_uuid ?? '',
            })),
          })
          queryClient.invalidateQueries({ queryKey: ['search', 'organizations'] })
        },
        onError: err => {
          console.error('Update failed', err)
          setSavingFields([])
        },
      }
    )
  }

  const handleUpdateWebsite = (fieldName: string, newValue: string) => {
    if (organization[fieldName] !== newValue && isValidWebsite(newValue)) {
      setSavingFields(prev => [...prev, fieldName])

      const updates = [
        {
          primaryKey: 'org_uuid',
          tableName: 'organizations',
          itemId: PK,
          fieldName,
          newValue,
          dealIds: organization?.dealIds, // Include dealIds if needed
        },
      ]
      updateWebsite(
        { updates: updates },
        {
          onSuccess: () => {
            console.log('Update successful')
            showSnackbar(`Update Successful for ${fieldName}`, 'success')
            setChangedFields({})
            setSavingFields(prev => prev.filter(field => field !== fieldName))

            // queryClient.invalidateQueries({ queryKey: ['organization', PK] })
            syncOrganizationFields(queryClient, organization, [{ fieldName: 'website', newValue: newValue }])
          },
          onError: err => {
            console.error('Update failed', err)
            setSavingFields(prev => prev.filter(field => field !== fieldName))
          },
        }
      )
    }
  }
  const handleCancel = () => {
    setChangedFields({})
    setSavingFields([])
    setIsEditing(false)
    setCustomProductDrafts([])
    setSelectedProduct(null)
    setTeamDetailsValue(organization?.team_details ?? [])
  }
  ////////////////////////////////////////////////////////////////////
  const ACTIVE_PRODUCT_STATUSES = new Set([
    'Active',
    'Active (system)',
    'Month To Month',
    '30 Day Notice',
    'In Progress',
  ])

  const n = (v: any) => Number(v) || 0
  const totals = useMemo(() => {
    const products = Array.isArray(viewProducts) ? viewProducts : []

    const allTotal = products.reduce((sum, p) => sum + n(p?.price), 0)

    const activeRunningTotal = products.reduce((sum, p) => {
      const isActive = ACTIVE_PRODUCT_STATUSES.has(String(p?.status ?? ''))
      return isActive && isRunningToday(p) ? sum + n(p?.price) : sum
    }, 0)

    return { allTotal, activeRunningTotal, productCount: products.length }
  }, [viewProducts])
  ////////////////////////////////////////////////////////////////////
  const childSections = useMemo(() => {
    return !isContactsLoading && !isUsersLoading
      ? fieldsConfigSections(
          organization,
          organizations,
          contacts,
          handleChange,
          mergedCustomDrafts,
          handleAddCustomProduct,
          handleEditCustomDraft,
          handleRemoveCustomDraft,
          selectedProduct,
          selectCustomProduct
        )
      : []
  }, [organization, organizations, contacts, handleChange, mergedCustomDrafts, isContactsLoading, isUsersLoading])

  const organizationFieldsSections = useMemo(() => {
    if (!childSections.length) return []
    return isParent ? parentConfigFromChildSections(childSections, totals) : childSections
  }, [isParent, childSections, totals])
  if (isItemsLoading) return <GROBxLoader  message='Syncing Organization'/>
  return (
    <Box display="flex" flexDirection="column" height="100%" sx={{ ...gridStyles(theme), position: 'relative' }}>
      {/* Scrollable content area */}
      <Box
        flex={1}
        overflow="auto"
        sx={{
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            boxShadow: `inset 0 0 6px ${alpha(theme.palette.text.primary, 0.3)}`,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.background.scrollbar_thumb,
            borderRadius: '6px',
            '&:hover': {
              backgroundColor: 'text.secondary',
            },
          },
        }}
      >
        <ProfileCardAdapter
          sections={organizationFieldsSections}
          isEditing={isEditing}
          entity={organization}
          getFieldValue={getFieldValue}
          onChange={handleChange}
          changedFields={changedFields}
          savingFields={savingFields}
          ReadOnlyFieldComponent={UniversalReadOnlyField} // Use Universal instead of ReadOnlyField
          renderContext={{ contacts, users, organizations, existingCustomProducts, isParent }}
        />
      </Box>
      {/* Sticky footer for edit/save controls */}
      {!isContactsLoading && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: theme.palette.background.paper,
            px: 3,
            py: 1.5,
            boxShadow: `0 -2px 10px ${alpha(theme.palette.text.primary, 0.3)}`,
            zIndex: 10,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Grid container justifyContent="space-between" alignItems="center" spacing={1}>
            {isEditing ? (
              <>
                <Grid item>
                  <Typography color={theme.palette.text.primary} variant="subtitle1">
                    Changed Fields:
                  </Typography>
                </Grid>

                <Grid item sx={{ maxWidth: '60%', flexGrow: 1, overflow: 'hidden' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    {Object.entries(changedFields)
                      .filter(([_, meta]) => meta.isDirty)
                      .map(([key]) => (
                        <Box
                          component="span"
                          key={key}
                          sx={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            border: `1px solid ${theme.palette.text.primary}`,
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            color: theme.palette.text.secondary,
                            background: 'transparent',
                          }}
                        >
                          {prettifyKey(key)}
                        </Box>
                      ))}
                  </Box>
                </Grid>
                <Grid item>
                  <Stack direction={'row'} gap={2}>
                    <CancelButton onClick={handleCancel} disabled={false} />
                    <SaveChangesButton
                      onClick={handleUpdate}
                      disabled={!hasDirtyOrgFields && !hasDirtyCustomDrafts}
                      variant="contained"
                      sx={{ color: theme.palette.text.primary }}
                    >
                      Save Changes
                    </SaveChangesButton>
                  </Stack>
                </Grid>
              </>
            ) : (
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <ProfileFooter
                  items={[
                    { label: 'Spend', value: spend ? formatCurrency(spend) : 0, valueColor: 'text.success' },
                    {
                      label: 'Gross Profit',
                      value: grossProfit ? formatCurrency(grossProfit) : 0,
                      valueColor: 'text.success',
                    },
                    {
                      label: 'Monthly Total',
                      value: monthlyTotal ? formatCurrency(monthlyTotal) : 0,
                      valueColor: 'text.success',
                    },
                    { label: 'Total', value: total ? formatCurrency(total) : 0, valueColor: 'text.success' },
                  ]}
                  padding={0}
                  showBorder={false}
                />
                <EditIconButton onEdit={() => setIsEditing(true)} />
              </Grid>
            )}
          </Grid>
        </Box>
      )}
    </Box>
  )
}

export default OrganizationProfile
